import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { UseMutationReturnType, UseQueryReturnType } from '@tanstack/vue-query'
import { isAxiosError } from 'axios'
import { storeToRefs } from 'pinia'
import { computed, nextTick, toValue } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import api from '@dashboard/services/axiosApi'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useTeamStore } from '@dashboard/stores/teamStore'

export type AppSyncState = 'importing' | 'ready' | 'delayed' | 'reconnect'

export interface AppSync {
  state: AppSyncState
  livemode: boolean
  lastSyncedAt: string | null
  message: string | null
  /** When the next attempt runs, while importing or delayed. */
  nextAttemptAt: string | null
}

/** The account a connection uses, for apps whose grant sees several. */
export interface AppAccountRef {
  id: string
  name: string
}

/** One account a connection's grant can see (`GET apps/{key}/accounts`). */
export interface AppAccount extends AppAccountRef {
  detail: string | null
}

export interface AppConnectionStatus {
  connected: boolean
  verifiedAt: string | null
  sync: AppSync | null
  /** Present only for apps with accounts. */
  account?: AppAccountRef | null
  /** Connected without a chosen account; such a connection never syncs. */
  needsAccount?: boolean
}

export interface CredentialField {
  key: string
  label: string
  help: string | null
  placeholder?: string
}

export interface AppSetupGuide {
  url: string
  linkLabel: string
  steps: string[]
  /** Collapsed-guide toggle in the manage view; falls back to `linkLabel`. */
  toggleLabel?: string
}

/** Server-authored dialog copy; each field is omitted when the app has none. */
export interface CredentialsCopy {
  intro?: string
  testDataNote?: string
  importsSummary?: string
  replaceHelp?: string
}

/** Server-authored OAuth copy; each field is omitted when the app has none. */
export interface OAuthCopy {
  intro?: string
  importsSummary?: string
  connectLabel?: string
}

export type AppActivation =
  | { kind: 'configToggle' }
  /** `startUrl` is an API-host path (open with `backendUrl()`); null in the public demo. */
  | ({ kind: 'oauth'; startUrl: string | null } & OAuthCopy)
  | ({
      kind: 'credentials'
      scheme: { kind: 'apiKey'; fields: CredentialField[] }
      guide: AppSetupGuide | null
    } & CredentialsCopy)

export interface AppListing {
  key: string
  listing: {
    name: string
    logo: string
    description: string
    category: string
    /** Plan feature needed to connect; listed so the UI can offer an upgrade. */
    requiredFeature?: string
  }
  activation: AppActivation
  resources: boolean
  connection: AppConnectionStatus | null
}

export interface AppStatusTag {
  severity: 'info' | 'success' | 'warn' | 'danger'
  value: string
}

const SYNC_TAGS: Record<AppSyncState, AppStatusTag> = {
  importing: { severity: 'info', value: 'Importing' },
  ready: { severity: 'success', value: 'Connected' },
  delayed: { severity: 'warn', value: 'Sync delayed' },
  reconnect: { severity: 'danger', value: 'Key rejected' }
}

/**
 * Connected apps without sync show "Connected"; a rejected key never does. A connection still waiting for its account
 * never syncs, so it asks for one instead. OAuth apps have no key, so their rejected grant asks for a reconnect.
 */
export const appStatusTag = (
  connection: AppConnectionStatus | null,
  activation?: AppActivation['kind']
): AppStatusTag | null => {
  if (!connection?.connected) {
    return null
  }
  if (connection.needsAccount) {
    return { severity: 'warn', value: 'Choose an account' }
  }
  const state = connection.sync?.state ?? 'ready'
  return state === 'reconnect' && activation === 'oauth'
    ? { severity: 'danger', value: 'Reconnect needed' }
    : SYNC_TAGS[state]
}

export const SYNC_POLL_MS = 5_000

export class AppConnectionError extends Error {
  readonly status?: number
  readonly fieldErrors: Record<string, string>

  constructor(message: string, status?: number, fieldErrors: Record<string, string> = {}) {
    super(message)
    this.name = 'AppConnectionError'
    this.status = status
    this.fieldErrors = fieldErrors
  }
}

/**
 * Never retain Axios errors or request bodies. 422 field messages pass through: the server authors them as constants
 * and never forwards upstream text.
 */
export const appConnectionError = (error: unknown): AppConnectionError => {
  const status = isAxiosError(error) ? error.response?.status : undefined
  const messages: Record<number, string> = {
    404: 'App is not connected or is unavailable',
    409: 'Reconnect this app',
    422: 'Invalid credentials',
    502: 'This app is temporarily unavailable. Please try again.',
    503: 'This app took too long to respond. Please try again.'
  }
  const errors: unknown = isAxiosError(error) ? error.response?.data?.errors : undefined
  const fieldErrors: Record<string, string> =
    status === 422 && errors && typeof errors === 'object' && !Array.isArray(errors)
      ? Object.fromEntries(
          Object.entries(errors).map(([key, value]: [string, unknown]) => {
            const first: unknown = Array.isArray(value) ? value[0] : value
            return [key, typeof first === 'string' && first.trim() ? first : 'Invalid credentials']
          })
        )
      : {}
  return new AppConnectionError(
    Object.values(fieldErrors)[0] ??
      ((status && messages[status]) || 'Unable to load or update apps. Please try again.'),
    status,
    fieldErrors
  )
}

/**
 * `realtime: true` observes the shared directory without the 5-second import poll and never refetches on its own
 * (window focus, remount): it loads once per team, and realtime events, `refetch()` or invalidation refresh it instead.
 * Other observers of the same query keep their own freshness.
 */
export const useAppsQuery = (
  enabled: MaybeRefOrGetter<boolean> = true,
  { realtime = false }: { realtime?: boolean } = {}
) => {
  const { currentTeamPid } = storeToRefs(useConfigStore())
  return useQuery<AppListing[], AppConnectionError>({
    queryKey: computed(() => ['apps', currentTeamPid.value]),
    queryFn: async ({ signal }) => {
      try {
        const response = await api.get<AppListing[]>(`/api/${currentTeamPid.value}/apps`, { signal })
        return response.data
      } catch (error) {
        throw appConnectionError(error)
      }
    },
    enabled: computed(() => !!currentTeamPid.value && toValue(enabled)),
    ...(realtime ? { staleTime: Infinity } : {}),
    refetchInterval: (query) =>
      !realtime && query.state.data?.some((app) => app.connection?.sync?.state === 'importing') ? SYNC_POLL_MS : false,
    retry: false
  })
}

export const useApps = (): AppsState => {
  const { currentTeamPid } = storeToRefs(useConfigStore())
  const { isDemo } = storeToRefs(useTeamStore())
  const queryClient = useQueryClient()
  const appsQuery = useAppsQuery()
  const isConnected = (key: string) =>
    appsQuery.data.value?.some((app) => app.key === key && app.connection?.connected) === true

  const updateConnection = async (teamPid: number, key: string, connection: AppConnectionStatus) => {
    const queryKey = ['apps', teamPid]
    await queryClient.cancelQueries({ queryKey })
    queryClient.setQueryData<AppListing[]>(queryKey, (apps) =>
      apps?.map((app) => (app.key === key ? { ...app, connection } : app))
    )
    await nextTick()
    for (const kind of ['resources', 'accounts']) {
      const filter = { queryKey: [...queryKey, key, kind], exact: true }
      if (connection.connected) {
        void queryClient.invalidateQueries(filter, { cancelRefetch: false })
      } else {
        void queryClient.resetQueries(filter)
      }
    }
  }

  const connectMutation = useMutation<
    { teamPid: number; key: string; connection: AppConnectionStatus },
    AppConnectionError,
    { key: string; credentials: Record<string, string> }
  >({
    gcTime: 0,
    mutationFn: async (variables: { key: string; credentials: Record<string, string> }) => {
      const teamPid = currentTeamPid.value
      const key = variables.key
      try {
        if (typeof teamPid !== 'number' || isDemo.value) {
          throw new Error('A project is required')
        }
        const response = await api.put<AppConnectionStatus>(`/api/${teamPid}/apps/${encodeURIComponent(key)}`, {
          ...variables.credentials
        })
        return { teamPid, key, connection: response.data }
      } catch (error) {
        throw appConnectionError(error)
      } finally {
        // Vue Query retains mutation variables even after rejection.
        for (const field of Object.keys(variables.credentials)) {
          variables.credentials[field] = ''
        }
      }
    },
    onSuccess: ({ teamPid, key, connection }) => updateConnection(teamPid, key, connection)
  })

  const disconnectMutation = useMutation<{ teamPid: number; key: string }, AppConnectionError, string>({
    mutationFn: async (key: string) => {
      const teamPid = currentTeamPid.value
      try {
        if (typeof teamPid !== 'number' || isDemo.value) {
          throw new Error('A project is required')
        }
        await api.delete(`/api/${teamPid}/apps/${encodeURIComponent(key)}`)
        return { teamPid, key }
      } catch (error) {
        throw appConnectionError(error)
      }
    },
    onSuccess: ({ teamPid, key }) => updateConnection(teamPid, key, { connected: false, verifiedAt: null, sync: null })
  })

  const selectAccountMutation = useMutation<
    { teamPid: number; key: string; connection: AppConnectionStatus },
    AppConnectionError,
    { key: string; accountId: string }
  >({
    mutationFn: async ({ key, accountId }: { key: string; accountId: string }) => {
      const teamPid = currentTeamPid.value
      try {
        if (typeof teamPid !== 'number' || isDemo.value) {
          throw new Error('A project is required')
        }
        const response = await api.put<AppConnectionStatus>(`/api/${teamPid}/apps/${encodeURIComponent(key)}/account`, {
          accountId
        })
        return { teamPid, key, connection: response.data }
      } catch (error) {
        throw appConnectionError(error)
      }
    },
    onSuccess: async ({ teamPid, key, connection }) => {
      await updateConnection(teamPid, key, connection)
      // Choosing an account starts its sync; the directory carries the new state.
      void queryClient.invalidateQueries({ queryKey: ['apps', teamPid], exact: true })
    }
  })

  return { appsQuery, isConnected, connectMutation, disconnectMutation, selectAccountMutation }
}

export interface AppsState {
  appsQuery: UseQueryReturnType<AppListing[], AppConnectionError>
  isConnected: (key: string) => boolean
  connectMutation: UseMutationReturnType<
    { teamPid: number; key: string; connection: AppConnectionStatus },
    AppConnectionError,
    { key: string; credentials: Record<string, string> },
    unknown
  >
  disconnectMutation: UseMutationReturnType<{ teamPid: number; key: string }, AppConnectionError, string, unknown>
  selectAccountMutation: UseMutationReturnType<
    { teamPid: number; key: string; connection: AppConnectionStatus },
    AppConnectionError,
    { key: string; accountId: string },
    unknown
  >
}
